import { History, Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

import { AssistantChat } from "./AssistantChat";
import { ConversationList } from "./ConversationList";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AssistantPanel({ open, onOpenChange }: Props) {
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const startNew = () => {
    setConversationId(null);
    setShowHistory(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="p-0" data-testid="assistant-panel">
        <SheetHeader className="flex-row items-center gap-2 space-y-0">
          <SheetTitle className="flex-1">Assistente</SheetTitle>
          <SheetDescription className="sr-only">
            Converse com o assistente sobre os dados da sua frota.
          </SheetDescription>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            aria-label="Histórico"
            aria-pressed={showHistory}
            onClick={() => setShowHistory((v) => !v)}
            data-testid="assistant-history-toggle"
          >
            <History className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            aria-label="Nova conversa"
            onClick={startNew}
            data-testid="assistant-panel-new"
          >
            <Plus className="h-4 w-4" />
          </Button>
          <Link
            to="/assistente"
            className="text-xs underline"
            onClick={() => onOpenChange(false)}
            data-testid="assistant-fullscreen-link"
          >
            Tela cheia
          </Link>
        </SheetHeader>
        {/* O chat fica montado enquanto o histórico aparece: desmontar no meio de um stream perderia a resposta. */}
        <div className="relative min-h-0 flex-1">
          <div className={cn("h-full", showHistory && "hidden")}>
            <AssistantChat
              conversationId={conversationId}
              onConversationCreated={setConversationId}
            />
          </div>
          {showHistory && (
            <div className="absolute inset-0 bg-background">
              <ConversationList
                selectedId={conversationId}
                onSelect={(id) => {
                  setConversationId(id);
                  setShowHistory(false);
                }}
                onNew={startNew}
              />
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
